import React, { useEffect, useState } from 'react';
import { createRoot, Root } from 'react-dom/client';
import styles from './Toast.module.scss';

interface ToastProps {
  message: string;
  duration?: number;
  onClose?: () => void;
}

// 淡出动画时长（需与 Toast.module.scss 中的 fadeOut 动画时长保持一致）
// 如果修改 CSS 动画时长，请同步修改此常量
const FADE_OUT_DURATION = 200; // ms

// Toast 最小显示时长（避免闪烁）
const MIN_DURATION = 100; // ms

// Toast 默认显示时长
const DEFAULT_DURATION = 2000; // ms

const Toast: React.FC<ToastProps> = ({ message, duration = DEFAULT_DURATION, onClose }) => {
  const [fadeOut, setFadeOut] = useState(false);

  useEffect(() => {
    // 参数校验：正确区分无效数值（NaN）和有效数值（包括 0）
    // 1. 如果 duration 是 NaN（如传入 undefined、null、非数字字符串），使用默认值
    // 2. 如果 duration 是有效数字（包括 0、负数），取 MAX(MIN_DURATION, duration)
    // 这样可以正确处理 duration=0 的情况，使其被限制为 MIN_DURATION 而非 DEFAULT_DURATION
    const parsedDuration = Number(duration);
    const validDuration = isNaN(parsedDuration) ? DEFAULT_DURATION : Math.max(MIN_DURATION, parsedDuration);

    // 使用 ReturnType<typeof setTimeout> 获取正确的定时器类型
    // 在浏览器环境中，setTimeout 返回 number 类型
    let fadeOutTimer: ReturnType<typeof setTimeout> | null = null;
    let closeTimer: ReturnType<typeof setTimeout> | null = null;

    // 设置淡出定时器
    fadeOutTimer = setTimeout(() => {
      setFadeOut(true);
      // 等待淡出动画完成后关闭
      // FADE_OUT_DURATION 需与 CSS 中的 fadeOut 动画时长保持一致
      closeTimer = setTimeout(() => {
        onClose?.();
      }, FADE_OUT_DURATION);
    }, validDuration);

    // Cleanup：清理所有定时器，避免内存泄漏
    return () => {
      if (fadeOutTimer !== null) clearTimeout(fadeOutTimer);
      if (closeTimer !== null) clearTimeout(closeTimer);
    };
  }, [duration, onClose]);

  return (
    <div className={styles.toastContainer}>
      <div className={`${styles.toast} ${fadeOut ? styles.fadeOut : ''}`}>{message}</div>
    </div>
  );
};

// Toast管理器
class ToastManager {
  private container: HTMLDivElement | null = null;
  private root: Root | null = null;
  // 浏览器环境下 setTimeout 返回 number 类型
  private currentTimeout: ReturnType<typeof setTimeout> | null = null;

  // 公共方法放在前面
  show(message: string, duration: number = DEFAULT_DURATION): void {
    // 参数校验：正确区分无效数值（NaN）和有效数值（包括 0）
    const parsedDuration = Number(duration);
    const validDuration = isNaN(parsedDuration) ? DEFAULT_DURATION : Math.max(MIN_DURATION, parsedDuration);

    // 清除之前的Toast
    this.hide();

    const container = this.getContainer();
    this.root = createRoot(container);

    const handleClose = () => {
      this.hide();
    };

    this.root.render(<Toast message={message} duration={validDuration} onClose={handleClose} />);
  }

  hide(): void {
    if (this.currentTimeout !== null) {
      clearTimeout(this.currentTimeout);
      this.currentTimeout = null;
    }

    if (this.root) {
      this.root.unmount();
      this.root = null;
    }

    // 使用可选链简化判断
    this.container?.parentNode?.removeChild(this.container);
    this.container = null;
  }

  // 私有方法放在后面
  private getContainer(): HTMLDivElement {
    if (!this.container) {
      this.container = document.createElement('div');
      this.container.id = 'toast-container';
      document.body.appendChild(this.container);
    }
    return this.container;
  }
}

// 导出单例实例
const toastManager = new ToastManager();

export const toast = {
  show: (message: string, duration?: number) => toastManager.show(message, duration),
  hide: () => toastManager.hide(),
};

export default Toast;
